import _extends from '@babel/runtime/helpers/extends';
import _jsx from '@babel/runtime/helpers/jsx';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import React from 'react';
import classNames from 'classnames';
import { Card, CardContent } from '@andes/card';
import { useVppContext } from '@vpp-frontend-components/vpp-context';
import { Icon } from '@vpp-frontend-components/icon';
import { StyledLabel } from '@vpp-frontend-components/styled-label';
import { ThumbnailMultiple, Thumbnail as Thumbnail$1 } from '@andes/thumbnail';
import { Image } from '@vpp-frontend-components/image';
const namespace$2 = 'ui-product-suggestions-information';

/**
 * Information component
 * @type {import('../../index.d.ts').InformationProps}
 */
const Information = _ref => {
  var _action$label;
  let {
    title,
    description,
    action
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    className: `${namespace$2}`
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: `${namespace$2}__content`
  }, void 0, (title === null || title === void 0 ? void 0 : title.text) && /*#__PURE__*/_jsx("div", {
    className: `${namespace$2}__content__title`
  }, void 0, /*#__PURE__*/_jsx(StyledLabel, {
    as: "p",
    text: title.text,
    color: title.color,
    font_family: title.font_family,
    font_size: title.font_size
  })), (description === null || description === void 0 ? void 0 : description.text) && /*#__PURE__*/_jsx("div", {
    className: `${namespace$2}__content__description`
  }, void 0, /*#__PURE__*/_jsx(StyledLabel, {
    as: "p",
    text: description.text,
    color: description.color,
    font_family: description.font_family,
    font_size: description.font_size
  }))), (action === null || action === void 0 ? void 0 : action.target) && (action === null || action === void 0 || (_action$label = action.label) === null || _action$label === void 0 ? void 0 : _action$label.text) && /*#__PURE__*/_jsx("div", {
    className: `${namespace$2}__target`
  }, void 0, /*#__PURE__*/_jsx(StyledLabel, {
    as: "a",
    className: `${namespace$2}__target__link`,
    href: action.target,
    text: action.label.text,
    color: action.label.color,
    font_family: action.label.font_family,
    font_size: action.label.font_size
  })));
};
const namespace$1 = 'ui-product-suggestions-thumbnail';

/**
 * Thumbnail component
 * @type {import('../..').ThumbnailProps}
 */
const Thumbnail = _ref => {
  let {
    pictures,
    moreLabel,
    type = 'stacked',
    containerSize = '32',
    modifier = 'circle'
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    className: `${namespace$1}`
  }, void 0, /*#__PURE__*/_jsx(ThumbnailMultiple, {
    type: type === null || type === void 0 ? void 0 : type.toLowerCase(),
    containerSize: containerSize,
    modifier: modifier === null || modifier === void 0 ? void 0 : modifier.toLowerCase(),
    overflow: moreLabel
  }, void 0, pictures.map(item => {
    var _item$url, _item$url2;
    return /*#__PURE__*/_jsx(Thumbnail$1, {}, (_item$url = item.url) === null || _item$url === void 0 ? void 0 : _item$url.src, /*#__PURE__*/_jsx(Image, {
      className: `${namespace$1}__image`,
      src: (_item$url2 = item.url) === null || _item$url2 === void 0 ? void 0 : _item$url2.src,
      alt: item.alt
    }));
  })));
};

/**
 * Convierte un color en formato hexadecimal a sus componentes RGB.
 *
 * @param {string} hex - El valor de color en formato hexadecimal (ejemplo: '#FF5733').
 * @returns {{ r: number, g: number, b: number }} Un objeto con las propiedades r, g, y b que representan los componentes rojo, verde y azul en formato decimal.
 *
 * @example
 * // Retorna { r: 255, g: 87, b: 51 }
 * hexToRgb('#FF5733');
 *
 * @example
 * // Retorna { r: 0, g: 255, b: 255 }
 * hexToRgb('#00FFFF');
 */
function hexToRgb(hex) {
  let hexCode = hex.replace(/^#/, '');
  if (hexCode.length === 3) {
    // Si el hex es abreviado (por ejemplo #FFF), expande a 6 dígitos
    hexCode = hexCode.split('').map(character => character + character).join('');
  }

  // Extrae los componentes R, G, B
  const r = parseInt(hexCode.substring(0, 2), 16);
  const g = parseInt(hexCode.substring(2, 4), 16);
  const b = parseInt(hexCode.substring(4, 6), 16);
  return {
    r,
    g,
    b
  };
}
const styleMapping = {
  background_color: {
    getStyleValue: (value, namespace) => ({
      [`${namespace}-background-color--${value}`]: value
    }),
    type: 'class-style'
  },
  background_color_hex: {
    getStyleValue: value => ({
      backgroundColor: value
    }),
    type: 'inline-style'
  },
  background_gradient_color: {
    getStyleValue: (value, namespace) => ({
      [`${namespace}-background-gradient-color--${value}`]: value
    }),
    type: 'class-style'
  },
  background_gradient_color_hex: {
    getStyleValue: value => {
      const {
        r,
        g,
        b
      } = hexToRgb(value);
      return {
        background: `linear-gradient(
          262.59deg,
          rgba(${r} ${g} ${b} / 14%) -0.95%,
          rgba(${r} ${g} ${b} / 6%) 100%
        )`
      };
    },
    type: 'inline-style'
  },
  shadow: {
    getStyleValue: value => ({
      shadow: value.toLowerCase()
    })
  },
  shadow_color: {
    getStyleValue: (value, namespace) => ({
      [`${namespace}-border-color--${value}`]: value
    }),
    type: 'class-style'
  },
  shadow_color_hex: {
    getStyleValue: value => ({
      borderColor: value
    }),
    type: 'inline-style'
  },
  thumbnail_shadow_color: {
    getStyleValue: (value, namespace) => ({
      [`${namespace}-thumbnail-shadow-color--${value}`]: value
    }),
    type: 'class-style'
  }
};
const defaultValues = {
  background_color: 'WHITE',
  shadow: 'OUTLINE',
  shadow_color: 'GRAY_100'
};

/**
 * Obtiene los estilos de la tarjeta en función de los estilos proporcionados y un espacio de nombres.
 *
 * @param {Object} styles - Un objeto que contiene los estilos personalizados para la tarjeta.
 * @param {string} namespace - Un espacio de nombres que puede ser utilizado para generar clases específicas.
 *
 * @returns {Object} Un objeto que contiene los estilos combinados:
 *  - `className` {string} - Las clases CSS resultantes, combinadas con las clases predeterminadas.
 *  - `style` {Object} - Un objeto que contiene los estilos en línea combinados.
 *
 * @example
 * const customStyles = {
 *   backgroundColor: 'red',
 *   border: '1px solid black'
 * };
 * const namespace = 'miTarjeta';
 *
 * const result = getCardStyles(customStyles, namespace);
 * // result puede ser algo como:
 * // {
 * //   className: 'clase-predeterminada miTarjeta-clase',
 * //   style: { backgroundColor: 'red', border: '1px solid black' }
 * // }
 */
const getCardStyles = (styles, namespace) => {
  const stylesWithDefaults = _objectSpread(_objectSpread({}, defaultValues), styles);
  return Object.entries(stylesWithDefaults).reduce((acc, _ref) => {
    let [key, value] = _ref;
    const mapping = styleMapping[key];
    const hasGradientColor = !!(stylesWithDefaults !== null && stylesWithDefaults !== void 0 && stylesWithDefaults.background_gradient_color || stylesWithDefaults !== null && stylesWithDefaults !== void 0 && stylesWithDefaults.background_gradient_color_hex);
    const isActiveGradientBakground = hasGradientColor && (key === 'background_color' || key === 'background_color_hex');
    if (!key.endsWith('_hex') && stylesWithDefaults[`${key}_hex`] || isActiveGradientBakground) {
      return acc;
    }
    if (mapping !== null && mapping !== void 0 && mapping.getStyleValue) {
      switch (mapping === null || mapping === void 0 ? void 0 : mapping.type) {
        case 'class-style':
          acc.className = classNames(acc.className, mapping.getStyleValue(value, namespace));
          break;
        case 'inline-style':
          acc.style = _objectSpread(_objectSpread({}, acc.style), mapping.getStyleValue(value));
          break;
        default:
          Object.assign(acc, mapping.getStyleValue(value));
          break;
      }
    }
    return acc;
  }, {});
};
const _excluded = ["className"];
const namespace = 'ui-product-suggestions';

/**
 * ProductSuggestions component
 * @type {import('../index.d.ts').ProductSuggestions}
 */
const ProductSuggestions = _ref => {
  let {
    className,
    content,
    thumbnails,
    action,
    background_color,
    shadow = 'NONE',
    runCatchErrorBoundary = () => {},
    style
  } = _ref;
  try {
    var _thumbnails$pictures, _action$icon, _action$icon2;
    /* eslint-disable react-hooks/rules-of-hooks */
    const {
      staticProps
    } = useVppContext();
    const {
      deviceType
    } = staticProps;
    const {
      title,
      description
    } = content || {};
    const mergedStyles = _objectSpread({
      shadow,
      background_color
    }, style);
    const _getCardStyles = getCardStyles(mergedStyles, namespace),
      {
        className: cardClass
      } = _getCardStyles,
      props = _objectWithoutProperties(_getCardStyles, _excluded);
    const cardClasses = classNames(className, namespace, `${namespace}--${deviceType}`, cardClass, {
      [`${namespace}--${deviceType}__with-styles`]: style
    });
    return /*#__PURE__*/React.createElement(Card, _extends({
      className: cardClasses,
      paddingSize: 0,
      "data-testid": `${namespace}-card`
    }, props), /*#__PURE__*/_jsx(CardContent, {}, void 0, /*#__PURE__*/_jsx("div", {
      className: `${namespace}__content`
    }, void 0, /*#__PURE__*/_jsx(Information, {
      title: title,
      description: description,
      action: action
    }), (thumbnails === null || thumbnails === void 0 || (_thumbnails$pictures = thumbnails.pictures) === null || _thumbnails$pictures === void 0 ? void 0 : _thumbnails$pictures.length) > 0 && /*#__PURE__*/_jsx(Thumbnail, {
      pictures: thumbnails.pictures,
      moreLabel: thumbnails.more_quantity,
      type: thumbnails.container_type,
      containerSize: thumbnails.container_size,
      modifier: thumbnails.container_style
    }), (action === null || action === void 0 ? void 0 : action.icon) && /*#__PURE__*/_jsx(Icon, {
      "data-testid": `${namespace}-icon`,
      id: (_action$icon = action.icon) === null || _action$icon === void 0 ? void 0 : _action$icon.id,
      className: `${namespace}-icon`,
      color: (_action$icon2 = action.icon) === null || _action$icon2 === void 0 ? void 0 : _action$icon2.color
    }))));
    /* eslint-enable react-hooks/rules-of-hooks */
  } catch (error) {
    /* istanbul ignore next */
    return runCatchErrorBoundary(error);
  }
};
ProductSuggestions.displayName = 'ProductSuggestions';
export { ProductSuggestions };
